import './thesaurus-term-create-form.scss'

import _ from 'lodash'
import { map, switchMap } from 'rxjs/operators'
import { EMPTY, Observable, of } from 'rxjs'
import Component from "vue-class-component"
import { faPlus } from '@fortawesome/free-solid-svg-icons'

import { SearchTitleOptions, ThesaurusTerm, createTranslatedString, getTranslatedString } from '@skinsoft/types'
import { preferredTerm } from '@skinsoft/selectors/thesaurus-term-selectors'
import { ThesaurusTermDataStore } from '@skinsoft/entity'
import { ReactiveVue } from "@skinsoft/reactive-vue"
import SearchableDropDown, { SEARCHABLE_DROPDOWN_ITEM_SELECTED, SEARCHABLE_DROPDOWN_SELECTED_ITEMS_CHANGED, SearchableDropDownItem, SearchableDropDownSearchResult } from '../../searchable-drop-down'

export const THESAURUS_TERM_CREATED = 'thesaurus-term-created'

@Component({
    components: {
        sSearchableDropDown: SearchableDropDown
    },
    props: ['thesaurusIdentifier', 'thesaurusId'],
    template: require('./thesaurus-term-create-form.html')
})
export default class ThesaurusTermCreateForm extends ReactiveVue {
    private readonly SEARCHABLE_DROP_DOWN_PAGE_SIZE = 5
    private thesaurusIdentifier: string
    private thesaurusId: string
    
    private preferedTerm: string = ''
    private synonyms: {key: string, value: string}[] = []
    private genericTermId: string = ''
    private specificTerms: string[] = []
    
    private genericTermChildren: ThesaurusTerm[] = [] // to show specificTerms fields
    private open: boolean = false

    private plusIcon = faPlus

    private $thesaurusTermDataStore: ThesaurusTermDataStore

    private isOnline: boolean = window.navigator.onLine

    private SEARCHABLE_DROPDOWN_ITEM_SELECTED = SEARCHABLE_DROPDOWN_ITEM_SELECTED
    private SEARCHABLE_DROPDOWN_SELECTED_ITEMS_CHANGED = SEARCHABLE_DROPDOWN_SELECTED_ITEMS_CHANGED

    private mounted() {
        // we use event listener's for connectivity status because it enable to have components that updates when the network status changes
        window.addEventListener('online', this.updateConnexionStatus)
        window.addEventListener('offline', this.updateConnexionStatus)
        this.addSynonym()
    }

    private async createThesaurusTerm(keepEditing: boolean) {
        if(window.navigator.onLine) {
            const body = {
                thesaurusTermCS: {
                    preferredTerm: createTranslatedString(this.getAppLocale(), this.preferedTerm),
                    synonyms: this.synonyms.filter(synonym => synonym.value.trim()).map(synonym => createTranslatedString(this.getAppLocale(), synonym.value)),
                },
                thesaurusTermSkin: {
                    genericTerm: this.genericTermId,
                    thesaurus: this.thesaurusId,
                    isForUnimarc: false,
                },
            }
    
            this.$thesaurusTermDataStore.create(undefined, body as Partial<ThesaurusTerm>, this.getAppLocale()).pipe(
                switchMap(thesaurusTerm => {
                    if(this.specificTerms) {
                        return this.$thesaurusTermDataStore.setSpecificTerms(thesaurusTerm.id, _.uniq(this.specificTerms))
                    }
                    return EMPTY
                })
            ).subscribe(() => {
                this.resetFields()
                this.$emit(THESAURUS_TERM_CREATED)
            })

            if(!keepEditing) {
                this.toggleEdit()
            }
        }
    }

    private addSynonym() {
        this.synonyms.push({
            key: performance.now().toString(),
            value: '',
        })
    }

    private onGenericTermSelected(genericTerm: { id: string, text: string }) {
        this.genericTermId = genericTerm.id
        this.$thesaurusTermDataStore.fetchChildren(genericTerm.id, this.getAppLocale(), 0, 1).subscribe(childrenTerms => {
            this.genericTermChildren = childrenTerms
        })
    }

    private onSpecificTermsChanged(specificTerms: SearchableDropDownItem[]) {
        this.specificTerms = specificTerms.map(term => term.id)
    }

    private searchThesaurusTerms(filterText: string, page: number = 0, size: number = 7, excludedItems?: SearchableDropDownItem[]): Observable<SearchableDropDownSearchResult> {
        const thesaurusTermEntityName = this.$thesaurusTermDataStore.entityName
        const thesaurusTermTypeString = thesaurusTermEntityName.charAt(0).toUpperCase() + thesaurusTermEntityName.slice(1)
        const excludedIds = excludedItems?.map(item => item.id)

        const searchOptions: SearchTitleOptions = {
            types: [thesaurusTermTypeString], 
            filterText: filterText, 
            pageNumber: page, 
            defaultFilter: this.thesaurusId, 
            size: size, 
            excludedIds: excludedIds, 
            esPath: 'thesaurusTermSkin.thesaurus.ref', 
            language: this.getAppLocale(),
            isForUnimarc: false,
        }

        return this.$thesaurusTermDataStore.searchTitles(searchOptions).pipe(
            map(data => {
                const items: SearchableDropDownItem[] = data.results.map(
                    title => {
                        return (
                            {
                                id: title.id,
                                text: getTranslatedString(title.genericEntityTitle, this.getAppLocale()),
                            })
                    }
                )
                const numberOfExtraPages = data.totalSize % size > 0 ? 1 : 0
                const nbPages = Math.floor(data.totalSize / size) + numberOfExtraPages

                return {
                    items,
                    nbPages
                }
            }),
        )
    }

    private searchThesaurusTermChildren(parentId: string, filterText: string, page: number = 0, size: number = 7, excludedItems?: SearchableDropDownItem[]): Observable<SearchableDropDownSearchResult> {
        const excludedIds = excludedItems?.map(item => item.id)

        return this.$thesaurusTermDataStore.fetchOne(parentId).pipe(
            switchMap(thesaurusTerm => {
                // @ts-ignore
                return this.$thesaurusTermDataStore.searchChildren(parentId, thesaurusTerm.pathIds, this.getAppLocale(), filterText, false, page, size, excludedIds)
            }),
            map(response => {
                const data = response.data ?? response.allData ?? []
                const items: SearchableDropDownItem[] = data.map(
                    thesaurusTerm => {
                        return {
                            id: thesaurusTerm.id,
                            text: getTranslatedString(preferredTerm(thesaurusTerm), this.getAppLocale()),
                        }
                    }
                )
                const numberOfExtraPages = response.totalSize % size > 0 ? 1 : 0
                const nbPages = Math.floor(response.totalSize / size) + numberOfExtraPages

                return {
                    items,
                    nbPages
                }
            }),
        )
    }

    private searchGenericTermChildren(filterText: string, page: number = 0, size: number = 7, excludedItems?: SearchableDropDownItem[]): Observable<SearchableDropDownSearchResult> {
        if(this.genericTermId) {
            return this.searchThesaurusTermChildren(this.genericTermId, filterText, page, size, excludedItems)
        } else {
            return of({
                items: [],
                nbPages: NaN,
            })
        }
    }

    private toggleEdit() {
        this.open = !this.open
    }

    private resetFields() {
        this.preferedTerm = ''
        this.synonyms = [{
            key: performance.now().toString(),
            value: '',
        }]
        this.genericTermId = ''
        this.genericTermChildren = []
        this.specificTerms = []
    }

    private closeForm() {
        this.toggleEdit()
        this.resetFields()
    }

    private updateConnexionStatus() {
        this.isOnline = window.navigator.onLine
    }
}